import './styles.css';
import type {
  BBox,
  BuildProgress,
  LobbyPublic,
  RaceResult,
  RegionRequest,
  ServerMessage,
  StopPreview,
  Tour,
  TourSummary,
  World,
} from '../shared/types.js';
import { gridSpawn } from '../shared/spawn.js';
import { Net, type NetStatus } from './net.js';
import { carAudio, type AudioDebug } from './audio.js';
import { ambience } from './ambience.js';
import { Game, type HudData } from './game/game.js';
import { Hud } from './ui/hud.js';
import { BigMap } from './ui/bigMap.js';
import { el } from './ui/dom.js';
import { serverText, t } from './i18n/i18n.js';
import { CAR_COLORS, LobbyScreen, type FixtureInfo } from './ui/lobbyScreen.js';
import { FrontDoor, type Pick, type VoteResult } from './ui/frontDoor.js';
import type { PreviewPending, PreviewResult, RegionEstimate } from './ui/regionPicker.js';
import { AudioSettings } from './ui/audioSettings.js';
import { GraphicsSettings } from './ui/graphicsSettings.js';
import { PauseMenu } from './ui/pauseMenu.js';
import { musicPlayer } from './music.js';
import { StagingScreen } from './ui/staging.js';
import { Podium } from './ui/podium.js';
import { TourBuilder } from './ui/tourBuilder.js';
import { RaceWizard } from './ui/raceWizard.js';
import { applyTheme } from './ui/theme.js';
import { AccountPanel, AccountSession, type Driver } from './ui/account.js';
import { LeaderboardPanel } from './ui/leaderboard.js';
import { Chat } from './ui/chat.js';
import { MenuBackdrop } from './render/menuBackdrop.js';
import { loadSavedRegions, saveRegion } from './savedRegions.js';

/**
 * App shell: owns the three screens, the socket and the game, and moves between them as
 * the server's lobby phase changes.
 *
 * The server is the authority on race state, so this file deliberately contains no
 * decisions about who won — it reacts to `lobby`, `world`, `countdown`, `snapshot` and
 * `finished` messages and puts the right thing on screen.
 */

const NAME_KEY = 'osm-racer.name';
const COLOR_KEY = 'osm-racer.color';

/**
 * How long a world payload may take before this client gives up on it. Generous: a first
 * race in a big city over a phone connection is a few megabytes, and giving up on a
 * download that was going to arrive costs somebody their race.
 */
const WORLD_FETCH_TIMEOUT_MS = 120_000;
/** Each poll is a few hundred bytes; once a second keeps the bar moving with a tile a minute. */
const PREVIEW_POLL_MS = 1000;
/** How often the perf graph under the minimap is redrawn. */
const PERF_GRAPH_MS = 100;

/** Exposed on `window.osmRacer` for the end-to-end tests. */
export interface TestHooks {
  getGame(): Game | null;
  getLobby(): LobbyPublic | null;
  getPlayerId(): string | null;
  /** Headless browsers have no ears; this is how the suite checks the engine is running. */
  getAudio(): AudioDebug;
}

class App {
  private readonly root: HTMLElement;
  private readonly net = new Net();
  private readonly hud = new Hud({
    onMinimapZoom: (step) => this.minimapZoom(step),
    onMinimapMode: () => this.minimapMode(),
    onMinimapOpen: () => this.toggleBigMap(),
  });
  private readonly bigMap = new BigMap({
    onOpenChange: (open) => {
      this.syncControlsBlocked();
      if (this.bigMapTimer !== null) window.clearInterval(this.bigMapTimer);
      this.bigMapTimer = null;
      // A few times a second is enough to follow a car on a map at street scale, and it
      // costs Leaflet a marker move rather than the frame budget.
      if (open) {
        this.bigMapTimer = window.setInterval(() => {
          if (this.game) this.bigMap.update(this.game.mapSnapshot());
        }, 250);
      }
      ambience.play(open ? 'menu-open' : 'menu-close');
    },
  });
  private bigMapTimer: number | null = null;

  private readonly front: FrontDoor;
  private readonly lobby: LobbyScreen;
  private readonly backdrop: MenuBackdrop;
  private readonly staging: StagingScreen;
  private readonly podium = new Podium();
  private readonly tourBuilder: TourBuilder;
  /** One map for every race anybody draws: the front door's, the lobby's and each tour stop. */
  private readonly wizard = new RaceWizard({
    onPreview: (region, onProgress, signal) => this.preview(region, onProgress, signal),
    onEstimate: (bbox, signal) => this.estimate(bbox, signal),
  });
  private readonly gameScreen: HTMLElement;
  private readonly badges: HTMLElement;
  private readonly canvas: HTMLCanvasElement;
  private readonly loading: HTMLElement;
  private readonly loadingDetail: HTMLElement;
  private readonly loadingFill: HTMLElement;
  private readonly pauseMenu: PauseMenu;
  private readonly audioSettings: AudioSettings;
  private readonly accountSession = new AccountSession();
  /** Who is driving. One copy, kept here, shown by the pill and asked for by the docket. */
  private driver: Driver = {
    name: readStored(NAME_KEY, ''),
    color: Number(readStored(COLOR_KEY, String(CAR_COLORS[0]))) || CAR_COLORS[0],
  };
  private readonly accountPanel = new AccountPanel(this.accountSession, {
    // Reads `this.board` at click time, not at construction time, which is the only reason
    // these two can point at each other from adjacent field initialisers.
    onOpenBoard: () => this.board.open(),
    getDriver: () => this.driver,
    onDriverChange: (driver) => this.setDriver(driver),
  });
  private readonly board = new LeaderboardPanel({
    onSignIn: () => this.accountPanel.open(),
  });
  private readonly chat = new Chat({
    onSend: (text) => this.net.send({ t: 'chat', text }),
    onTypingChange: () => this.syncControlsBlocked(),
  });

  private game: Game | null = null;
  private lobbyState: LobbyPublic | null = null;
  private currentWorldId: string | null = null;
  private announcedCheckpoint = 0;
  private perfDrawnAt = 0;
  private fixtures: FixtureInfo[] = [];
  /**
   * What the renderer is currently lit for. Weather arrives on every lobby broadcast and
   * changes perhaps once an hour, and rebuilding a rain field on every ready-state change
   * would be both a stutter and a visible reset of the rain.
   */
  private appliedWeather: string | null = null;

  /**
   * What was chosen at the front door, held until there is a lobby to apply it to.
   *
   * The choice is made before the socket is even open, so it cannot be sent with the join.
   * It is applied on `welcome` instead, and only if this client turned out to be the host —
   * joining somebody else's room by code and then silently changing their region would be
   * an act of war.
   */
  private pendingPick: Pick | null = null;

  constructor(mount: HTMLElement) {
    const params = new URLSearchParams(location.search);
    const initialCode = (params.get('code') ?? '').toUpperCase().replace(/[^A-Z0-9]/g, '');

    this.front = new FrontDoor({
      initialName: this.driver.name,
      initialColor: this.driver.color,
      initialCode,
      onStart: (pick, name, color) => {
        this.pendingPick = pick;
        this.join('NEW', name, color);
      },
      onJoin: (code, name, color) => {
        this.pendingPick = null;
        this.join(code, name, color);
      },
      onNewTour: () => void this.openTourBuilder(null),
      onEditTour: (id) => void this.openTourBuilder(id),
      onDraw: () => void this.drawAtFrontDoor(),
      onPreview: (region) => this.preview(region),
      onSignIn: () => this.accountPanel.open(),
      onDriverChange: (name, color) => this.setDriver({ name, color }),
      onVote: (id, vote) => this.voteTour(id, vote),
    });

    this.lobby = new LobbyScreen({
      onReady: (ready) => this.net.send({ t: 'ready', ready }),
      onRegion: (region, title) => this.net.send({ t: 'region', region, ...(title ? { title } : {}) }),
      onDraw: (initial) => void this.drawInLobby(initial),
      onAiCount: (count) => this.net.send({ t: 'aiCount', count }),
      onMode: (mode) => this.net.send({ t: 'mode', mode }),
      onWeatherChoice: (choice) => this.net.send({ t: 'weatherChoice', choice }),
      onStart: () => this.net.send({ t: 'start' }),
      onLeave: () => this.leave(),
      onTour: (tourId) => this.net.send({ t: 'tour', tourId }),
      onEditTour: (tourId) => void this.openTourBuilder(tourId),
      onCancelGeneration: () => this.net.send({ t: 'cancelGeneration' }),
    });

    this.staging = new StagingScreen({
      onReady: (ready) => this.net.send({ t: 'ready', ready }),
      onForceStart: () => this.net.send({ t: 'start' }),
    });

    this.tourBuilder = new TourBuilder({
      wizard: this.wizard,
      onSignIn: () => this.accountPanel.open(),
      onSaved: (tour) => {
        void this.loadTours();
        // Saving a tour is nearly always the last step before racing it, so selecting it
        // saves the one click that would otherwise sit between the two.
        this.net.send({ t: 'tour', tourId: tour.id });
      },
      onDeleted: (id) => {
        void this.loadTours();
        if (this.lobbyState?.tour?.id === id) this.net.send({ t: 'tour', tourId: null });
      },
    });

    this.canvas = el('canvas', { class: 'game-canvas' });
    this.loadingDetail = el('p', { class: 'loading-detail' }, t('Contacting the server…'));
    this.loadingFill = el('div', { class: 'loading-bar-fill' });
    this.loading = el(
      'div',
      { class: 'loading' },
      el('h2', { class: 'loading-title' }, t('Building the city')),
      this.loadingDetail,
      el('div', { class: 'loading-bar' }, this.loadingFill),
    );
    this.gameScreen = el(
      'div',
      { class: 'screen screen-game is-hidden' },
      this.canvas,
      this.hud.root,
      // Both sit over the live scene rather than replacing it: the grid needs the fly-over
      // playing behind it, and the podium fades the city down rather than cutting it out.
      this.staging.root,
      this.podium.root,
      this.chat.raceRoot,
      this.bigMap.root,
      this.loading,
    );

    // Settings live behind Escape now, on every screen rather than only in a race — you
    // want the volume before you have created a lobby, not just once you are driving.
    this.audioSettings = new AudioSettings();
    this.pauseMenu = new PauseMenu({
      content: this.audioSettings.root,
      graphics: new GraphicsSettings().root,
      onOpenChange: (open) => {
        this.audioSettings.setActive(open);
        // The race carries on behind the blur, but the keyboard belongs to the menu while
        // it is up — otherwise reading the controls list means holding the throttle down.
        this.syncControlsBlocked();
        ambience.play(open ? 'menu-open' : 'menu-close');
      },
    });

    // Two pills that share a row, so that a long account name pushes the board button along
    // rather than sliding underneath it. They are re-parented by `showScreen` rather than
    // floating in a corner: on the front door they belong in the masthead, in the lobby in
    // the header, and over a race they go back to hanging off the corner of the screen.
    this.badges = el('div', { class: 'corner-badges' }, this.board.badge, this.accountPanel.badge);
    this.backdrop = new MenuBackdrop();

    this.root = el(
      'div',
      { class: 'app' },
      this.backdrop.root,
      this.front.root,
      this.lobby.root,
      this.gameScreen,
      this.pauseMenu.root,
      this.tourBuilder.root,
      // After the builder, so a race being added to a tour opens over the top of it.
      this.wizard.root,
      this.accountPanel.root,
      this.board.root,
      this.badges,
    );
    mount.append(this.root);
    this.lobby.attachChat(this.chat.lobbyRoot);

    // Whoever is signed in decides which tours come back flagged as theirs, so the list is
    // refetched on every change rather than patched — it is one small request.
    this.accountSession.subscribe(() => {
      const account = this.accountSession.current.account;
      // Signed in, the driver is called what the account is: one name on the grid and on the
      // board. A name the server would clip is clipped here first, so the two agree.
      if (account && account.name.slice(0, 16) !== this.driver.name) {
        this.setDriver({ ...this.driver, name: account.name.slice(0, 16) });
      }
      this.tourBuilder.setAccount(this.accountSession.current.account?.name ?? null);
      this.front.setSignedIn(Boolean(this.accountSession.current.account));
      void this.loadTours();
      // Signing in or out changes which row on the board is yours, and whether the footer
      // is an invitation or a placing.
      this.board.invalidate();
    });
    void this.accountSession.refresh();
    applyTheme(this.driver.color);

    this.lobby.root.classList.add('is-hidden');
    this.lobby.setSavedRegions(loadSavedRegions());
    this.showScreen('home');

    this.net.on((msg) => this.onMessage(msg));
    this.net.onStatus((status, detail) => this.onStatus(status, detail));

    void this.loadFixtures();
    // The soundtrack manifest is a static file, so this is one fetch and it can fail freely:
    // no soundtrack installed is a perfectly normal server.
    void musicPlayer.loadCatalogue();
    window.addEventListener('keydown', this.onGlobalKey);

    // Every browser refuses to start an AudioContext until the user has interacted with the
    // page, so the first click or key press anywhere is what brings the engine up.
    //
    // It keeps trying until the context is actually `running`, rather than unbinding after
    // one go. `resume()` can reject — a gesture the browser considers stale, a policy that
    // wanted a different kind of interaction — and the old one-shot version treated that as
    // survivable when it was terminal: the context stayed suspended for the rest of the
    // session and every Web Audio sound was gone for good, with no way back short of a
    // reload. Music would still play, because that is an `<audio>` element and lives outside
    // the context entirely, so the symptom was the confusing one: music but no engine, no
    // countdown, no skids.
    const unlock = () => {
      carAudio.unlock();
      if (carAudio.debug().contextState !== 'running') return;
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
    };
    window.addEventListener('pointerdown', unlock);
    window.addEventListener('keydown', unlock);

    // Test hook. The Playwright suite needs to pose the camera and read race state from
    // inside the render loop; there is no other way to assert that the car actually
    // moved, or to take a repeatable aerial screenshot of a generated region.
    (window as unknown as { osmRacer: TestHooks }).osmRacer = {
      getGame: () => this.game,
      getLobby: () => this.lobbyState,
      getPlayerId: () => this.net.playerId,
      getAudio: () => carAudio.debug(),
    };

    // Deep link: /?code=ABCD prefills the join box so a shared URL just works.
    if (initialCode) this.front.setCode(initialCode);
  }

  // -------------------------------------------------------------------------
  // screens
  // -------------------------------------------------------------------------

  private showScreen(which: 'home' | 'lobby' | 'game'): void {
    this.front.root.classList.toggle('is-hidden', which !== 'home');
    this.lobby.root.classList.toggle('is-hidden', which !== 'lobby');
    this.gameScreen.classList.toggle('is-hidden', which !== 'game');

    // The two pills follow the screen rather than sitting in a fixed corner over all three.
    const host =
      which === 'home' ? this.front.badgeSlot : which === 'lobby' ? this.lobby.badgeSlot : this.root;
    if (this.badges.parentElement !== host) host.append(this.badges);
    this.badges.classList.toggle('is-floating', which === 'game');

    // The menu city is a luxury, and the race is not: it gives up both its frames and its
    // meshes the moment there is an actual world to render.
    if (which === 'game') this.backdrop.sleep();
    else void this.backdrop.wake();

    if (which === 'game') this.game?.start();
    else this.game?.stop();
    this.pauseMenu.setInRace(which === 'game');
    if (which !== 'game') this.bigMap.close();
    if (which !== 'game') this.pauseMenu.setActions([]);
    // The shipped soundtrack is race music: it follows the game screen and stays out of the
    // lobby. A file or URL the player chose themselves is not gated this way — see
    // `music.ts`. Getting here always follows a click, so autoplay policy is satisfied.
    musicPlayer.setInRace(which === 'game');
  }

  private setLoading(visible: boolean, detail?: string, progress?: number): void {
    this.loading.classList.toggle('is-hidden', !visible);
    if (detail) this.loadingDetail.textContent = detail;
    if (progress !== undefined) {
      this.loadingFill.style.width = `${Math.round(Math.max(0, Math.min(1, progress)) * 100)}%`;
    }
  }

  private onStatus(status: NetStatus, detail?: string): void {
    const label =
      status === 'open'
        ? t('Connected · {ms} ms', { ms: this.net.latency })
        : status === 'connecting'
          ? t('Connecting…')
          : status === 'reconnecting'
            ? t('Reconnecting… {detail}', { detail: detail ?? '' }).trim()
            : t('Offline');
    this.pauseMenu.setConnection(label, status === 'open');
    // On the HUD, only trouble is worth a pixel: the ping already rides on the stats line
    // under the map, and a permanent "connected" badge was saying what a working game says
    // by working.
    this.hud.setNetTrouble(status === 'open' ? '' : label);
  }

  // -------------------------------------------------------------------------
  // lifecycle
  // -------------------------------------------------------------------------

  private async loadFixtures(): Promise<void> {
    try {
      const res = await fetch('/api/fixtures');
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = (await res.json()) as {
        regions: FixtureInfo[];
        maxRegionKm2: number;
        minRegionKm2?: number;
        offline: boolean;
      };
      this.fixtures = data.regions;
      this.wizard.setFixtures(data.regions);
      this.wizard.setLimits({
        maxAreaKm2: data.maxRegionKm2,
        ...(data.minRegionKm2 !== undefined ? { minAreaKm2: data.minRegionKm2 } : {}),
        offline: data.offline,
      });
      this.lobby.setFixtures(data.regions, data.maxRegionKm2);
      this.front.setFixtures(data.regions);
      await this.loadTours();
    } catch (err) {
      this.front.showError(
        t('Could not reach the game server ({error}). Is `npm run dev` running?', { error: (err as Error).message }),
      );
    }
  }

  private async loadTours(): Promise<void> {
    try {
      const res = await fetch('/api/tours');
      const data = (await res.json()) as { ok: boolean; tours?: TourSummary[]; maxStops?: number };
      this.lobby.setTours(data.tours ?? []);
      this.front.setTours(data.tours ?? []);
      this.tourBuilder.setFixtures(this.fixtures, data.maxStops ?? 8);
      void this.loadTourDetails(data.tours ?? []);
    } catch {
      // No tour list is a lobby with no saved cups in it, which is exactly what a fresh
      // server looks like. Nothing else on the page depends on this.
    }
  }

  /**
   * Every tour in full, so the lobby can offer each of their races as a place of its own. The
   * list only carries names; the regions come with the tour. A tour that will not load is
   * left out rather than holding up the rest.
   */
  private async loadTourDetails(tours: TourSummary[]): Promise<void> {
    const full = await Promise.all(
      tours.map(async (summary) => {
        try {
          const res = await fetch(`/api/tours/${encodeURIComponent(summary.id)}`);
          const body = (await res.json()) as { ok: boolean; tour?: Tour };
          return body.tour ?? null;
        } catch {
          return null;
        }
      }),
    );
    this.lobby.setTourDetails(full.filter((tour): tour is Tour => tour !== null));
  }

  private async voteTour(id: string, vote: -1 | 0 | 1): Promise<VoteResult> {
    try {
      const res = await fetch(`/api/tours/${encodeURIComponent(id)}/vote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ vote }),
      });
      const data = (await res.json()) as { ok: boolean; error?: string; up?: number; down?: number; myVote?: -1 | 0 | 1 };
      if (!data.ok) return { error: data.error ? serverText(data.error) : t('Could not vote.') };
      return { up: data.up ?? 0, down: data.down ?? 0, myVote: data.myVote ?? 0 };
    } catch (err) {
      return { error: (err as Error).message };
    }
  }

  private async openTourBuilder(tourId: string | null): Promise<void> {
    let tour: Tour | null = null;
    if (tourId) {
      try {
        const res = await fetch(`/api/tours/${encodeURIComponent(tourId)}`);
        const body = (await res.json()) as { ok: boolean; tour?: Tour };
        tour = body.tour ?? null;
      } catch {
        // Fall through and open a blank builder rather than nothing at all — but say so,
        // because silently offering "new tour" when someone asked to edit one is a lie.
      }
      if (!tour) {
        this.hud.toast(t('Could not load that tour. It may have been deleted.'), 'error');
        void this.loadTours();
        return;
      }
    }
    this.tourBuilder.open(tour);
  }

  /**
   * The driver changed — in the panel, the docket, or by signing in. Kept, shown, and, in a
   * lobby, told to everyone else, who would otherwise see the old name until the next race.
   */
  private setDriver(driver: Driver): void {
    // Whichever door the name came in by — the docket can be open before the session check
    // has answered — a signed-in driver is called what their account is.
    const account = this.accountSession.current.account;
    if (account) driver = { ...driver, name: account.name.slice(0, 16) };
    this.driver = driver;
    // A different car is a different palette: Material You's whole idea, with the car colour
    // standing in for a wallpaper. See `theme.ts`.
    applyTheme(driver.color);
    writeStored(NAME_KEY, driver.name);
    writeStored(COLOR_KEY, String(driver.color));
    this.front.setDriver(driver.name, driver.color);
    this.accountPanel.refreshDriver();
    if (this.lobbyState && this.net.playerId) {
      this.net.send({ t: 'profile', name: driver.name, color: driver.color });
    }
  }

  private join(code: string, name: string, color: number): void {
    writeStored(NAME_KEY, name);
    writeStored(COLOR_KEY, String(color));
    // A different lobby means the stored player id is meaningless; drop it so we join
    // fresh rather than being rejected as an unknown returning player.
    if (code !== 'NEW' && this.net.code && this.net.code !== code) this.net.forgetSession();
    this.net.connect(code, name, color);
  }

  /**
   * Turns what was picked at the front door into the lobby's actual state.
   *
   * Only the host may, and only ever their own new room: `pendingPick` is cleared the moment
   * it is used or the moment a join is made instead, so a reconnect into a room somebody
   * else has since re-pointed cannot re-apply a stale choice over the top of theirs.
   */
  private applyPick(lobby: LobbyPublic): void {
    const pick = this.pendingPick;
    this.pendingPick = null;
    if (!pick) return;
    const self = lobby.players.find((p) => p.id === this.net.playerId);
    if (!self?.host) return;

    if (pick.kind === 'tour') this.net.send({ t: 'tour', tourId: pick.id });
    // A built-in region is named by each client in its own language; a drawn one by the host.
    else this.net.send({ t: 'region', region: pick.region, ...(pick.region.fixture ? {} : { title: pick.title }) });
  }

  /** "Draw your own" on the shelf: the race first, then the docket and the lobby around it. */
  private async drawAtFrontDoor(): Promise<void> {
    const result = await this.wizard.open({
      mode: 'race',
      title: t('Draw your own race'),
      confirmLabel: t('Race here'),
    });
    if (!result) return;
    this.remember(result);
    this.front.chooseRegion(result.region, result.title);
  }

  /** The host drawing a region from the lobby, or reshaping the one already picked. */
  private async drawInLobby(initial: RegionRequest | null): Promise<void> {
    const free = this.lobbyState?.mode === 'free';
    const title = this.lobbyState?.regionTitle ?? undefined;
    const result = await this.wizard.open({
      mode: free ? 'free' : 'race',
      title: free
        ? initial
          ? t('Move the start')
          : t('Draw your own free drive')
        : initial
          ? t('Adjust the race')
          : t('Draw your own race'),
      confirmLabel: free ? t('Drive here') : t('Race here'),
      ...(initial ? { initial: { region: initial, ...(title ? { title } : {}) }, startAt: 'pins' as const } : {}),
    });
    if (!result) return;
    if (!result.region.fixture) this.remember(result);
    this.net.send({ t: 'region', region: result.region, ...(result.region.fixture ? {} : { title: result.title }) });
  }

  /** A drawn region goes on the lobby's saved shelf, so it never has to be drawn twice. */
  private remember(result: { region: RegionRequest; title: string; preview?: StopPreview }): void {
    this.lobby.setSavedRegions(saveRegion(result.title, result.region, result.preview));
  }

  private leave(): void {
    this.net.forgetSession();
    this.net.disconnect();
    this.game?.dispose();
    this.game = null;
    this.lobbyState = null;
    this.currentWorldId = null;
    this.appliedWeather = null;
    this.hud.reset();
    this.staging.hide();
    this.podium.hide();
    // The room is gone, and so is anything that was said in it.
    this.chat.reset();
    this.chat.replace([]);
    this.showScreen('home');
  }

  /**
   * The dry run: one POST, and then a poll a second until the build is done.
   *
   * A region already on the server answers the POST outright. A new one gets a job back, and
   * the poll is what keeps any single request short — the proxy in front of a real server
   * cuts a request off long before Overpass has finished with a big region. See `previews.ts`.
   */
  private async preview(
    region: RegionRequest,
    onProgress?: (progress: BuildProgress) => void,
    signal?: AbortSignal,
  ): Promise<PreviewResult> {
    let res = await fetch('/api/preview', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(region),
      signal,
    });
    let body = (await res.json()) as PreviewResult | PreviewPending;
    while ('pending' in body) {
      onProgress?.(body.progress);
      await new Promise((resolve) => setTimeout(resolve, PREVIEW_POLL_MS));
      signal?.throwIfAborted();
      res = await fetch(`/api/preview/${encodeURIComponent(body.job)}`, { signal });
      body = (await res.json()) as PreviewResult | PreviewPending;
    }
    return body;
  }

  /** How much of a box the server has on disk, and how long the rest would take to fetch. */
  private async estimate(bbox: BBox, signal?: AbortSignal): Promise<RegionEstimate | null> {
    const res = await fetch(`/api/estimate?bbox=${[bbox.south, bbox.west, bbox.north, bbox.east].join(',')}`, { signal });
    if (!res.ok) return null;
    return (await res.json()) as RegionEstimate;
  }

  // -------------------------------------------------------------------------
  // server messages
  // -------------------------------------------------------------------------

  private onMessage(msg: ServerMessage): void {
    switch (msg.t) {
      case 'welcome':
        this.lobbyState = msg.lobby;
        this.lobby.update(msg.lobby, this.net.playerId ?? '');
        // `generating` belongs with the lobby, not the game: there is no world to render
        // yet, so sending a player who joined mid-build to the game screen showed them a
        // black canvas — the one moment they most needed the panel saying what is going on.
        this.showScreen(
          msg.lobby.phase === 'lobby' || msg.lobby.phase === 'generating' ? 'lobby' : 'game',
        );
        // After the screen is up, not before: "draw your own" opens the map picker, and
        // Leaflet measures its container the moment it is built. Built into a hidden panel
        // it comes up with no size at all.
        this.applyPick(msg.lobby);
        this.updateUrl(msg.lobby.code);
        break;

      case 'lobby':
        this.onLobby(msg.lobby);
        break;

      case 'world':
        void this.loadWorld(msg.worldId, msg.url);
        break;

      case 'chat':
        this.chat.push(msg);
        // Your own line is not news to you, and the server relays it back like everyone
        // else's — the same reason the horn is not echoed to the player who sounded it.
        if (msg.id !== this.net.playerId) ambience.play('chat');
        break;

      case 'chatLog':
        this.chat.replace(msg.messages);
        break;

      case 'honk':
        // Positioned at whatever this client already believes about that car — the same
        // place it is drawing them. Nothing about where the sound comes from is on the wire.
        carAudio.playRemoteHonk(msg.id);
        break;

      case 'countdown':
        this.ensureGame().beginCountdown(msg.startsAt, msg.now, this.net.clock.toLocal(msg.startsAt));
        break;

      case 'snapshot':
        this.game?.applySnapshot(msg);
        break;

      case 'finished':
        this.onFinished(msg.results);
        // The server has just written this race into the record, so neither the panel
        // behind the podium nor the board should still be showing the count from before it.
        void this.accountSession.refresh();
        this.board.invalidate();
        break;

      case 'error':
        if (this.gameScreen.classList.contains('is-hidden')) this.front.showError(serverText(msg.message));
        else this.hud.toast(serverText(msg.message), 'error');
        // A rejected join leaves us with no session; go back so the player can retry.
        if (/no lobby|full|already started/i.test(msg.message)) {
          this.net.forgetSession();
          this.showScreen('home');
        }
        break;
    }
  }

  private onLobby(lobby: LobbyPublic): void {
    const previous = this.lobbyState;
    this.lobbyState = lobby;
    const localId = this.net.playerId ?? '';
    this.lobby.update(lobby, localId);
    this.game?.setRoster(lobby.players);
    this.applyWeather(lobby);
    this.announceRoster(previous, lobby);

    if (lobby.phase === 'lobby') {
      if (previous?.phase !== 'lobby') {
        this.game?.endFreeDrive();
        this.hud.reset();
        this.podium.hide();
        this.showScreen('lobby');
      }
      this.staging.hide();
      return;
    }

    this.syncStaging(lobby, previous);
    this.syncPauseActions(lobby);

    // A free drive moving on to its tour's next map: everyone keeps driving this one while
    // the next builds, and if it cannot be built they carry on here, told why.
    if (lobby.mode === 'free' && previous?.phase === 'racing' && lobby.phase === 'generating') {
      const next = lobby.tour?.stops[lobby.tour.index]?.title ?? '';
      this.hud.showBanner(t('Loading the next map: {title}', { title: next }), 3000);
    }
    if (lobby.mode === 'free' && lobby.error && lobby.error !== previous?.error && lobby.phase === 'racing') {
      this.hud.toast(serverText(lobby.error), 'error', 9000);
    }

    if (lobby.phase === 'racing' && previous?.phase !== 'racing') {
      if (lobby.mode === 'free') {
        this.startFreeDrive();
      } else {
        this.game?.beginRacing();
        this.hud.showGo();
      }
    }
  }

  /**
   * Into the car for a free drive — once there is a car to get into. The `lobby` that says
   * the drive is on and the download of its world race each other, and an arrival in the
   * middle of one only ever has the second, so both paths come here.
   */
  private startFreeDrive(): void {
    const game = this.game;
    if (!game?.hasWorld || this.currentWorldId !== this.lobbyState?.worldId) return;
    if (game.isFreeDrive) return;
    game.beginFreeDrive();
    this.staging.hide();
    this.podium.hide();
    this.hud.hideResults();
    this.hud.showBanner(t('Free drive'), 1600);
    const isHost = this.lobbyState?.players.find((p) => p.id === this.net.playerId)?.host ?? false;
    this.hud.toast(
      isHost
        ? document.documentElement.classList.contains('touch-active')
          ? t('No gates and no clock. Drive anywhere; end it for everyone from the menu.')
          : t('No gates and no clock. Drive anywhere; end it for everyone from the Esc menu.')
        : t('No gates and no clock. Drive anywhere; the host ends it.'),
      'info',
      7000,
    );
  }

  /** A free drive is ended — or left — from the Escape menu, the one place it can be. */
  private syncPauseActions(lobby: LobbyPublic): void {
    const free = lobby.mode === 'free' && lobby.phase === 'racing';
    this.pauseMenu.setInRace(!this.gameScreen.classList.contains('is-hidden'), free);
    if (!free) {
      this.pauseMenu.setActions([]);
      return;
    }
    const isHost = lobby.players.find((p) => p.id === this.net.playerId)?.host ?? false;
    const leave = el('button', { class: 'button button-ghost', type: 'button', id: 'leave-free-drive' }, t('Leave'));
    leave.addEventListener('click', () => {
      this.pauseMenu.close();
      this.leave();
    });
    if (!isHost) {
      this.pauseMenu.setActions([leave]);
      return;
    }
    const end = el('button', { class: 'button button-tonal', type: 'button', id: 'end-free-drive' }, t('End the free drive'));
    end.addEventListener('click', () => {
      this.pauseMenu.close();
      this.net.send({ t: 'restart' });
    });
    // A free drive through a tour moves on from here. Wraps from the last map to the first.
    const tour = lobby.tour;
    if (tour && tour.stops.length > 1) {
      const nextTitle = tour.stops[(tour.index + 1) % tour.stops.length].title;
      const next = el(
        'button',
        { class: 'button button-primary', type: 'button', id: 'next-map' },
        t('Next map: {title}', { title: nextTitle }),
      );
      next.addEventListener('click', () => {
        this.pauseMenu.close();
        this.net.send({ t: 'nextRace' });
      });
      this.pauseMenu.setActions([next, end, leave]);
      return;
    }
    this.pauseMenu.setActions([end, leave]);
  }

  /**
   * Somebody arrived, left, or said they were ready — as a sound, if one has been installed.
   *
   * Diffed against the previous broadcast because there is no message for any of it: `lobby`
   * is a whole-room snapshot. Nothing at all is announced for the first one, since the person
   * who just walked in is you, and a room with five people already in it would otherwise
   * greet you by opening five doors. A host adding five AI drivers at once is one broadcast
   * and five joins; the cue's own cooldown is what turns that back into a single sound.
   */
  private announceRoster(previous: LobbyPublic | null, lobby: LobbyPublic): void {
    if (!previous) return;
    const before = new Map(previous.players.map((p) => [p.id, p]));
    const after = new Map(lobby.players.map((p) => [p.id, p]));
    // Readiness means something in a room where people are deciding; the server also clears
    // and sets it around a race, and none of that is anybody pressing a button.
    const readyMatters = lobby.phase === 'lobby' || lobby.phase === 'staging';
    for (const [id, player] of after) {
      const was = before.get(id);
      if (!was) ambience.play('join');
      else if (readyMatters && player.ready && !was.ready) ambience.play('ready');
    }
    for (const id of before.keys()) if (!after.has(id)) ambience.play('leave');
  }

  /**
   * The grid.
   *
   * Two messages arrive for it and they can land in either order: `world`, which starts a
   * download, and the `lobby` that turns the phase to `staging`. So both paths call this,
   * and it starts the fly-over only once the meshes actually exist.
   */
  private syncStaging(lobby: LobbyPublic, previous: LobbyPublic | null): void {
    if (lobby.phase !== 'staging') {
      this.staging.hide();
      return;
    }
    const localId = this.net.playerId ?? '';
    this.hud.hideResults();
    this.podium.hide();

    const game = this.game;
    // Nothing of the grid until this client has the city to put behind it. The ready button
    // over a loading bar is the wrong thing twice: there is no fly-over to be ready *for*,
    // and pressing it would be answering a question about a world you cannot see yet.
    if (!game?.hasWorld) {
      this.staging.hide();
      return;
    }
    this.staging.update(lobby, localId, this.regionLabel(lobby));
    this.staging.show();

    // The field can change while everyone is on the grid — someone closes a tab — and the
    // server lays the grid out again at the countdown. Following it here keeps this client's
    // car on the slot the server thinks it is on.
    if (!game.isStaging || previous?.players.length !== lobby.players.length) this.placeOnGrid();
    if (!game.isStaging) game.beginStaging();
  }

  private applyWeather(lobby: LobbyPublic): void {
    const weather = lobby.weather;
    const key = weather ? `${weather.kind}|${weather.phase}|${weather.sunAzimuth}|${weather.precipitation}` : 'none';
    if (key === this.appliedWeather) return;
    this.appliedWeather = key;
    this.game?.applyWeather(weather);
  }

  private regionLabel(lobby: LobbyPublic): string {
    if (!lobby.region) return '';
    if (lobby.region.fixture) {
      return serverText(this.fixtures.find((f) => f.id === lobby.region!.fixture)?.title ?? lobby.region.fixture);
    }
    return t('Custom region');
  }

  private ensureGame(): Game {
    if (this.game) return this.game;
    this.game = new Game(this.canvas, this.hud.minimapCanvas, {
      onState: (x, y, z, h, v) => this.net.send({ t: 'state', x, y, z, h, v }),
      onHud: (data) => this.onHud(data),
      onLocalFinish: () => this.hud.showBanner(t('Finished! Waiting for the others…'), 4000),
      onPerfWarning: (message) => this.hud.toast(message, 'warn', 9000),
      onStreet: (name, style) => {
        this.hud.street.setStyle(style);
        this.hud.street.show(name);
      },
      onHonk: () => this.honk(),
      onMenu: () => this.pauseMenu.toggle(),
      serverNow: () => this.net.clock.serverNow() ?? Date.now(),
    });
    this.syncMinimapButtons();
    return this.game;
  }

  private onHud(data: HudData): void {
    this.hud.update(data);
    // Announce each gate exactly once, and only forwards.
    if (data.checkpointsPassed > this.announcedCheckpoint) {
      this.announcedCheckpoint = data.checkpointsPassed;
      if (data.checkpointsPassed < data.checkpointsTotal) {
        this.hud.showBanner(
          t('Checkpoint {passed}/{total}', { passed: data.checkpointsPassed, total: data.checkpointsTotal }),
          1500,
        );
      }
    }
    // The perf graph is cosmetic, and it sits next to the frame it measures: ten times a
    // second is smooth enough to watch a stutter go by and leaves every other frame alone.
    const now = performance.now();
    if (this.game && now - this.perfDrawnAt >= PERF_GRAPH_MS) {
      this.perfDrawnAt = now;
      this.hud.perf.draw(this.game.frameTimes, this.game.fps, this.net.latencies);
    }
  }

  private async loadWorld(worldId: string, url: string): Promise<void> {
    this.showScreen('game');
    const game = this.ensureGame();

    if (this.currentWorldId === worldId && game.hasWorld) {
      this.placeOnGrid();
      this.setLoading(false);
      // Still reported, and this is the path a reconnect takes: the server is holding the
      // lights for everyone it has not heard from, and silence here would hold them forever.
      this.net.send({ t: 'worldStatus', worldId, status: 'ready' });
      return;
    }

    this.setLoading(true, t('Downloading the world…'), 0.15);
    try {
      // Bounded, because the whole grid now waits for this download. A fetch with no
      // deadline that never resolves — a proxy that accepted the connection and then went
      // quiet — would be a room nobody can start a race in and nobody can diagnose. Failing
      // is recoverable: the server stops waiting on a client that says it failed.
      const res = await fetch(url, { signal: AbortSignal.timeout(WORLD_FETCH_TIMEOUT_MS) });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      this.setLoading(true, t('Downloading the world…'), 0.5);
      const world = (await res.json()) as World;

      this.setLoading(true, t('Building meshes…'), 0.75);
      // Yield once so the progress bar actually paints before the synchronous
      // mesh build blocks the main thread for a few hundred milliseconds.
      await new Promise((resolve) => requestAnimationFrame(resolve));

      game.setLocalPlayer(this.net.playerId ?? '', this.localColor());
      game.setWorld(world);
      if (this.lobbyState) game.setRoster(this.lobbyState.players);
      this.currentWorldId = worldId;
      this.announcedCheckpoint = 0;
      this.placeOnGrid();
      // The scene kit outlives the world, so the weather has to be re-asserted rather than
      // re-applied: `appliedWeather` is about the *conditions* changing, and a new region
      // in the same conditions still needs its sky set.
      this.appliedWeather = null;
      if (this.lobbyState) this.applyWeather(this.lobbyState);

      this.setLoading(true, t('Ready'), 1);
      this.hud.perf.setTriangles(game.triangleCount);
      window.setTimeout(() => this.setLoading(false), 320);
      game.start();
      // Before the grid is synced, so the last client to finish building is also the one
      // whose message releases the lights — not one broadcast later.
      this.net.send({ t: 'worldStatus', worldId, status: 'ready' });
      // The staging broadcast usually beats the download; this is the other order.
      if (this.lobbyState) this.syncStaging(this.lobbyState, null);
      if (this.lobbyState?.mode === 'free' && this.lobbyState.phase === 'racing') this.startFreeDrive();
    } catch (err) {
      const reason =
        (err as Error).name === 'TimeoutError' ? t('it took too long') : (err as Error).message;
      this.setLoading(true, t('Could not load the world: {reason}', { reason }), 1);
      this.hud.toast(
        t('World failed to load: {reason}. Rejoin the lobby to try again.', { reason }),
        'error',
        12_000,
      );
      // Said out loud rather than left to a timeout, so the rest of the grid is told why it
      // is waiting and can go without this client instead of sitting on the line.
      this.net.send({ t: 'worldStatus', worldId, status: 'failed' });
    }
  }

  /** Puts the local car in its grid slot, matching the server's own calculation. */
  private placeOnGrid(): void {
    const game = this.game;
    const lobby = this.lobbyState;
    if (!game || !lobby || !game.hasWorld) return;
    const index = Math.max(0, lobby.players.findIndex((p) => p.id === this.net.playerId));
    const route = game.route;
    if (!route) return;
    const spawn = gridSpawn(route, index);
    game.setSpawn(spawn.x, spawn.z, spawn.heading);
    game.resetProgress();
  }

  private localColor(): number {
    const player = this.lobbyState?.players.find((p) => p.id === this.net.playerId);
    if (player) return player.color;
    const stored = Number(readStored(COLOR_KEY, ''));
    return Number.isFinite(stored) && stored > 0 ? stored : CAR_COLORS[0];
  }

  private onFinished(results: RaceResult[]): void {
    this.game?.endRace();
    this.staging.hide();
    const localId = this.net.playerId ?? '';
    const isHost = this.lobbyState?.players.find((p) => p.id === localId)?.host ?? false;
    const tour = this.lobbyState?.tour ?? null;

    // Winning is the server's word, not the local finish line's — this is the first moment
    // this client knows it. The cup screen takes precedence over the race that led to it.
    if (tour?.complete) ambience.play('podium');
    else if (results.find((r) => r.id === localId)?.position === 1) ambience.play('victory');

    // The cup is over. That is not a results table with an extra column on it — it takes
    // the whole screen, and the race that happened to be last is no longer the story.
    if (tour?.complete) {
      this.hud.hideResults();
      this.podium.show(tour, localId, isHost, {
        onAgain: () => {
          this.podium.hide();
          this.net.send({ t: 'start' });
        },
        onLobby: () => {
          this.podium.hide();
          this.net.send({ t: 'endTour' });
        },
        onLeave: () => this.leave(),
      });
      return;
    }

    const actions: HTMLElement[] = [];
    if (isHost && tour) {
      const next = el(
        'button',
        { class: 'button button-primary', type: 'button', id: 'tour-next' },
        t('Next race — {title}', { title: tour.stops[tour.index + 1]?.title ?? '' }),
      );
      next.addEventListener('click', () => {
        this.hud.hideResults();
        this.net.send({ t: 'nextRace' });
      });
      const end = el('button', { class: 'button', type: 'button' }, t('End the tour'));
      end.addEventListener('click', () => {
        this.hud.hideResults();
        this.net.send({ t: 'endTour' });
      });
      actions.push(next, end);
    } else if (isHost) {
      const again = el('button', { class: 'button button-primary', type: 'button' }, t('Back to lobby'));
      again.addEventListener('click', () => {
        this.hud.hideResults();
        this.net.send({ t: 'restart' });
      });
      actions.push(again);
    } else {
      actions.push(
        el(
          'p',
          { class: 'results-wait' },
          tour
            ? t('Waiting for the host to start the next race…')
            : t('Waiting for the host to start another race…'),
        ),
      );
    }
    const leave = el('button', { class: 'button button-ghost', type: 'button' }, t('Leave race'));
    leave.addEventListener('click', () => this.leave());
    actions.push(leave);

    this.hud.showResults(results, localId, actions, tour);
  }

  /**
   * The car listens to the keyboard unless something else is using it.
   *
   * Two things can claim it — the Escape menu and the chat box — and they can overlap, so
   * neither may simply set the flag: opening the menu while a message was half-typed and
   * then closing the menu would hand the throttle back to a page that is still typing. One
   * function, both reasons, recomputed whenever either changes.
   */
  private syncControlsBlocked(): void {
    this.game?.setControlsBlocked(this.pauseMenu.isOpen || this.chat.isTyping || this.bigMap.isOpen);
  }

  private updateUrl(code: string): void {
    const url = new URL(location.href);
    url.searchParams.set('code', code);
    history.replaceState(null, '', url.toString());
  }

  private onGlobalKey = (event: KeyboardEvent): void => {
    // Before the "is something focused" guard below, because the thing that is focused is
    // this box, and Escape is how you get out of it. Handled here rather than on the input
    // so that one place decides what Escape means.
    if (event.code === 'Escape' && this.chat.isTyping) {
      event.preventDefault();
      this.chat.closeRaceInput();
      return;
    }

    const active = document.activeElement;
    if (active && (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA')) return;

    // Enter starts a message, but only over a race: everywhere else Enter belongs to a form,
    // and a lobby already has a chat box sitting open.
    if (event.code === 'Enter' && !this.gameScreen.classList.contains('is-hidden') && !this.pauseMenu.isOpen) {
      event.preventDefault();
      this.chat.openRaceInput();
      return;
    }

    // Escape works everywhere, including the home screen — the volume is something you set
    // before a race, not during one. The tour builder is a modal of its own and gets first
    // claim on the key, so Escape always means "close the thing in front of me".
    if (event.code === 'Escape') {
      event.preventDefault();
      if (this.board.isOpen) this.board.close();
      else if (this.accountPanel.isOpen) this.accountPanel.close();
      else if (this.wizard.isOpen) this.wizard.cancel();
      else if (this.tourBuilder.isOpen) this.tourBuilder.close();
      else if (this.front.isDocketOpen) this.front.closeDocket();
      else if (this.bigMap.isOpen) this.bigMap.close();
      else this.pauseMenu.toggle();
      return;
    }

    // Mute works from any screen — you may well want it off before you are on the grid.
    if (event.code === 'KeyM') {
      const muted = carAudio.toggleMuted();
      this.hud.showBanner(muted ? t('Sound off') : t('Sound on'), 1200);
      return;
    }

    if (!this.game) return;
    if (this.gameScreen.classList.contains('is-hidden')) return;

    if (event.code === 'KeyR') {
      this.game.respawn();
      this.hud.showBanner(this.game.isFreeDrive ? t('Back on the street') : t('Respawned'), 1200);
    } else if (event.code === 'KeyF') {
      const next = this.game.cameraMode === 'chase' ? 'aerial' : 'chase';
      this.game.setCameraMode(next);
      this.hud.showBanner(next === 'aerial' ? t('Aerial view') : t('Chase view'), 1200);
    } else if (event.code === 'KeyH') {
      this.honk();
    } else if (event.code === 'KeyG') {
      this.toggleBigMap();
    } else if (event.code === 'KeyN') {
      this.minimapMode();
    } else if (event.code === 'BracketRight' || event.code === 'Equal') {
      this.minimapZoom(1);
    } else if (event.code === 'BracketLeft' || event.code === 'Minus') {
      this.minimapZoom(-1);
    }
  };

  /**
   * The horn. Only told to the server if it actually sounded here, so the two cooldowns
   * agree and a key held down is one message rather than sixty.
   */
  private honk(): void {
    if (carAudio.playHonk()) this.net.send({ t: 'honk' });
  }

  private toggleBigMap(): void {
    if (this.game) this.bigMap.toggle(this.game.mapSnapshot());
  }

  private minimapZoom(step: number): void {
    this.game?.zoomMinimap(step);
    this.syncMinimapButtons();
  }

  private minimapMode(): void {
    const mode = this.game?.toggleMinimapMode();
    if (mode) {
      this.hud.showBanner(mode === 'overview' ? t('Whole region') : t('Following your car'), 1200);
    }
    this.syncMinimapButtons();
  }

  private syncMinimapButtons(): void {
    const state = this.game?.minimapControls();
    if (state) this.hud.setMinimapState(state);
  }
}

function readStored(key: string, fallback: string): string {
  try {
    return localStorage.getItem(key) ?? fallback;
  } catch {
    return fallback;
  }
}

function writeStored(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* private browsing */
  }
}

const mount = document.getElementById('app');
if (!mount) throw new Error('#app is missing from index.html');
new App(mount);
